import UserProfile from "./UserProfile.js";
import {backendUrl} from "../../../Utils/backendOrigin.js";

class ApiService {
    #baseUrl
    #csrfPromise = null

    constructor() {
        this.#baseUrl = backendUrl('/accounts/')
        this.sessionGuard()

        this.intervalMs = 2 * 60 * 1000
        
        this.updateAccessToken()
    }

    async updateAccessToken() {
        setInterval(() => {
            if (UserProfile.isLoggedIn()) {
                this.refreshAccessTokens().catch(() => {})
            }
        }, this.intervalMs)
    }


    async sessionGuard() {
        try {
            const refreshOk = await this.refreshAccessTokens().catch(() => false)

            if (!refreshOk) {
                UserProfile.setLoggedIn(false)
                return
            }
            const authStatus = await this.checkAuth();

            if (authStatus.authenticated) {
                const initial = await this.getProfile().catch(() => null);
                if (initial) {
                    UserProfile.setLoggedIn(true);
                    UserProfile.setProfile(initial);
                }
            } else {
                UserProfile.setLoggedIn(false)
            }
        } catch (error) {
            console.error("Session check failed:", error.message);
            UserProfile.setLoggedIn(false)
        }
    }

    async logout(){
        return this.#request('logout/', null, 'POST')
    }

    async loginGuest() {
        return this.#request('guest/login/', null, 'POST')
    }

    async checkAuth() {
        try {
            const resp = await fetch(this.#baseUrl + 'check-auth/', {
                method: 'GET',
                credentials: 'include'
            })

            if (!resp.ok) {
                if ([401, 403].includes(resp.status)) {
                    const refreshOk = await this.refreshAccessTokens();
                    if (refreshOk) {
                        const retryResp = await fetch(this.#baseUrl + 'check-auth/', {
                            method: 'GET',
                            credentials: 'include'
                        });
                        
                        if (retryResp.ok) {
                            return await retryResp.json();
                        }
                    }
                }
                return {authenticated: false};
            }

            return await resp.json()
        } catch (error) {
            return { authenticated: false }
        }
    }

    async getProfile(force = false) {
        const headers = {}
        if (this._profileEtag && !force) {
            headers['If-None-Match'] = this._profileEtag
        }

        const resp = await fetch(this.#baseUrl + 'profile/', {
            method: 'GET',
            headers,
            credentials: 'include'
        })

        if (resp.status === 304) {
            return null;
        }

        if (!resp.ok) {
            let err = `(${resp.status})`
            try {
                err = (await resp.json()).detail || err
            } catch {

            }
            throw new Error(err)
        }
        const data = await resp.json()
        const etag = resp.headers.get('ETag')
        if (etag) {
            this._profileEtag = etag;
        }
        return data
    }

    async updateProfile(data) {
        const body = (data instanceof FormData) ? data : { ...data }
        return this.#request('update-profile/', body, 'PUT')
    }

    async #ensureCsrf() {
        if (this.#csrfPromise) {
            return this.#csrfPromise;
        }

        this.#csrfPromise = fetch(this.#baseUrl + 'get_csrf/', {
            method: 'GET',
            credentials: 'include'
        }).then(() => true).catch(() => false)
        return this.#csrfPromise
    }

    #getCookie(n) {
        return document.cookie.split('; ').find(c => c.startsWith(n + '='))?.split('=')[1]
    }

    /**
     * Make a request to the main backend
     * @param {string} path - The API endpoint path
     * @param {Object} body - Request body
     * @param {string} method - HTTP method
     * @returns {Promise<Object>} - Response from the backend
     */
    async #request(path, body=null, method='POST') {
        const headers = {}
        const isForm = body instanceof FormData
        if (!isForm) headers['Content-Type'] = 'application/json'

        if (['POST','PUT','PATCH','DELETE'].includes(method)) {
            await this.#ensureCsrf()
            const t = this.#getCookie('csrftoken')
            if (t) headers['X-CSRFToken'] = t
        }

        const resp = await fetch(this.#baseUrl + path, {
            method,
            headers,
            credentials: 'include',
            body: body ? (isForm ? body : JSON.stringify(body)) : undefined
        })

        if (!resp.ok) {
            const authenticationEndpoints = ['otp_request/', 'register/', 'otp_verify/', 'login/', '42/login/', 'google/login/']
            const skipRefresh = authenticationEndpoints.some(endpoint => path.includes(endpoint))
            
            if ([401, 403].includes(resp.status) && !skipRefresh) {
                const refreshSuccessful = await this.refreshAccessTokens()
                if (refreshSuccessful) {
                    return this.#request(path, body, method)
                }
            }

            let errorMessage = `(${resp.status})`
            try {
                errorMessage = (await resp.json()).detail || errorMessage
            } catch {
                
            }
            throw new Error(errorMessage)
        }

        let data = {}
        try {
            data = await resp.json()
        } catch {

        }
        return data
    }

    async refreshAccessTokens() {
        try {
            await this.#ensureCsrf();
            const csrfToken = this.#getCookie('csrftoken');
            
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (csrfToken) {
                headers['X-CSRFToken'] = csrfToken;
            }

            const r = await fetch(this.#baseUrl + 'refresh-token/', {
                method: 'POST',
                credentials: 'include',
                headers: headers
            });
            
            if (r.ok) {
                return true;
            } else {
                UserProfile.clearProfile()
                return false;
            }
        } catch (error) {
            console.error('[API] Token refresh error:', error);
            UserProfile.clearProfile()
            return false;
        }
    }
}

export default new ApiService()
